import React, { useContext, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import { sendReport } from "../../models/user.model"; // Import the sendReport function
import { UserContext } from "../../services/AppContext/appProvider";
import { useTheme } from "../../services/ThemeContext";
import {
  systemErrorAlertText,
  validatePhoneNumber
} from "../../utilities/utils";

type ReportProblemPopupProps = {
  onClose: () => void;
};

const ReportProblemPopup: React.FC<ReportProblemPopupProps> = ({ onClose }) => {
  const { theme } = useTheme();
  const { user } = useContext(UserContext);

  const [name, setName] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [cellNumber, setCellNumber] = useState(user?.mobileNumber || "");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Applying the same autofill styles as in the Contact Us page
    const styleElement = document.createElement("style");
    styleElement.textContent = `
      input:-webkit-autofill,
      textarea:-webkit-autofill {
        box-shadow: 0 0 0px 1000px ${
          theme === "dark" ? "#2f3029" : "#f7fafc"
        } inset !important;
        -webkit-text-fill-color: ${
          theme === "dark" ? "#f7fafc" : "#2f3029"
        } !important;
        background-clip: content-box !important;
      }
    `;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, [theme]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      Swal.fire({
        title: "Error",
        html:
          `<strong class="font-extrabold">Blank message cannot be submitted.</strong>` +
          systemErrorAlertText(),
        icon: "error",
        customClass: {
          confirmButton: "bg-secondary rounded-full text-base-100 px-6 py-2"
        },
        buttonsStyling: false
      });
      return;
    }

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      Swal.fire({
        title: "Error",
        html:
          `<strong class="font-extrabold">Invalid email address.</strong>` +
          systemErrorAlertText(),
        icon: "error",
        customClass: {
          confirmButton: "bg-secondary rounded-full text-base-100 px-6 py-2"
        },
        buttonsStyling: false
      });
      return;
    }

    // cell allow only numbers, +
    if (validatePhoneNumber(cellNumber) === false) {
      Swal.fire({
        title: "Error",
        html:
          `<strong class="font-extrabold">Invalid cell number.</strong>` +
          systemErrorAlertText(),

        icon: "error",
        customClass: {
          confirmButton: "bg-secondary rounded-full text-base-100 px-6 py-2"
        },
        buttonsStyling: false
      });
      return;
    }

    setLoading(true);

    Swal.fire({
      title: "Submitting...",
      text: "Please wait while we submit your report.",
      icon: "info",
      allowOutsideClick: false,
      showConfirmButton: false,
      customClass: {
        popup: "bg-success text-base-100"
      }
    });

    const response = await sendReport({
      fullName: name.trim(),
      email: email.trim(),
      cellNumber: cellNumber.trim(),
      message: message,
      type: "Issue"
    });
    Swal.close();

    if (response.rStatus === "success") {
      Swal.fire({
        title: "Submitted Successfully",
        text: response.rData,
        icon: "success",
        customClass: {
          confirmButton: "bg-secondary rounded-full text-base-100 px-6 py-2"
        },
        buttonsStyling: false
      });

      // Clear the form after successful submission
      setName("");
      setEmail("");
      setCellNumber("");
      setMessage("");
    } else {
      Swal.fire({
        title: "Error",
        html:
          `<strong class="font-extrabold">${response.message}</strong>` +
          systemErrorAlertText(),
        icon: "error",
        customClass: {
          confirmButton: "bg-secondary rounded-full text-base-100 px-6 py-2"
        },
        buttonsStyling: false
      });
    }

    setLoading(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    // To close the popup when clicking outside the popup
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const inputClasses =
    theme === "dark" ? "bg-success text-base-100" : "bg-base-100 text-neutral";

  return (
    <div
      className="fixed inset-0 left-0 top-0 z-[200] flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleOverlayClick}
    >
      <div
        className="relative w-11/12 rounded-lg bg-success bg-opacity-90 p-8 text-base-100 shadow-lg md:w-1/2 lg:w-5/12 xl:w-3/12"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-2 top-2 text-white"
          aria-label="Close"
        >
          <FaTimes size={24} />
        </button>

        <h2 className="mb-4 text-2xl">Report a Problem</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold" htmlFor="name">
              Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full rounded border px-3 py-2 ${inputClasses}`}
              required
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full rounded border px-3 py-2 ${inputClasses}`}
              required
            />
          </div>

          <div className="mb-4">
            <label
              className="mb-2 block text-sm font-bold"
              htmlFor="cellNumber"
            >
              Cell Number
            </label>
            <input
              id="cellNumber"
              type="text"
              value={cellNumber}
              onChange={(e) => setCellNumber(e.target.value)}
              className={`w-full rounded border px-3 py-2 ${inputClasses}`}
              required
            />
          </div>

          <div className="mb-4">
            <label className="mb-2 block text-sm font-bold" htmlFor="message">
              Message
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={`w-full rounded border px-3 py-2 ${inputClasses}`}
              rows={4}
              required
            ></textarea>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline btn-secondary btn-sm mt-4 rounded-lg px-5 text-base-100 hover:!text-base-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-secondary btn-sm mt-4 rounded-lg border-none px-5 text-base-100"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportProblemPopup;
